/**
 * useUserEvents — the cloud BE's /ws/user-events socket, held by Layout for the session: events
 * meant for THIS user, whatever site or page they are on. Today: Alert Actions' Dashboard pop-ups
 * (`alert:notify` — a site's Routing sent this alert to this person).
 *
 * One socket per open app, not per site, and it keeps no site tunnel warm. Reconnects with
 * backoff (1 s → 30 s) while signed in.
 */
import { useEffect, useRef } from 'react';
import type { AlertPopup } from '@trinetra/shared';
import { getStoredToken } from '../services/api.service';

export function useUserEvents(onNotify: (p: AlertPopup) => void): void {
  const notify = useRef(onNotify);
  notify.current = onNotify;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let delay = 1000;
    let stopped = false;

    const connect = () => {
      const token = getStoredToken();
      if (!token || stopped) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${proto}//${window.location.host}/ws/user-events?token=${encodeURIComponent(token)}`);
      ws.onopen = () => { delay = 1000; };
      ws.onmessage = (e) => {
        let msg: any;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg?.type === 'alert:notify' && msg.data) notify.current(msg.data as AlertPopup);
      };
      ws.onclose = () => {
        if (stopped) return;
        timer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30000);
      };
    };
    connect();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, []);
}
